
import React, { useState } from 'react';
import { Search, FileText, ChevronRight, Pencil, Trash2, UserX, UserCheck, ChevronDown, AlertTriangle, Users } from 'lucide-react';
import { motion } from 'motion/react';
import { Patient, ClinicalCycle, Therapist } from '../types';
import ReportModal from './ReportModal';

/** Profissional da clínica para o filtro (visível só ao administrador). */
export interface PatientListProfessional {
  id: string;
  name: string;
}

interface PatientListProps {
  patients: Patient[];
  onSelectPatient: (id: string, autoEdit?: boolean) => void;
  therapist: Therapist;
  filterAlertsOnly?: boolean;
  onClearFilterAlerts?: () => void;
  onDeletePatients?: (ids: string[]) => void;
  /** Exclusão individual já confirmada pelo modal desta lista. */
  onDeletePatient?: (id: string) => void | Promise<void>;
  onTogglePatientsStatus?: (ids: string[], status: 'Ativo' | 'Inativo') => void;
  /** Lista de profissionais para o filtro; só aparece quando `showProfessionalFilter`. */
  professionals?: PatientListProfessional[];
  showProfessionalFilter?: boolean;
}

const PatientList: React.FC<PatientListProps> = ({ 
  patients, 
  onSelectPatient, 
  therapist,
  filterAlertsOnly = false,
  onClearFilterAlerts,
  onDeletePatients,
  onDeletePatient,
  onTogglePatientsStatus,
  professionals = [],
  showProfessionalFilter = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState<{patient: Patient, cycle: ClinicalCycle} | null>(null);
  const [statusFilter, setStatusFilter] = useState<'Ativos' | 'Inativos' | 'Todos'>('Ativos');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // 'all' = "Todos os profissionais" (sem filtro).
  const [professionalFilter, setProfessionalFilter] = useState<string>('all');
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDeletePatient = async () => {
    if (!patientToDelete || !onDeletePatient) return;
    setIsDeleting(true);
    try {
      await onDeletePatient(patientToDelete.id);
      setSelectedIds(prev => prev.filter(id => id !== patientToDelete.id));
      setPatientToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasActiveAlert = (patient: Patient) => {
    if (patient.cycles && patient.cycles.length > 0) {
      const lastCycle = patient.cycles[patient.cycles.length - 1];
      return lastCycle.subjectiveDistress >= 8 || lastCycle.functionalImpairment >= 8;
    }
    return false;
  };

  const filteredPatients = patients.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Filtro por profissional (administrador): combina com status e busca.
    const matchesProfessional =
      !showProfessionalFilter || professionalFilter === 'all' || p.assignedProfessionalId === professionalFilter;
    if (filterAlertsOnly) {
      return matchesSearch && matchesProfessional && hasActiveAlert(p);
    }
    return matchesSearch && matchesProfessional;
  });

  const displayedPatients = filteredPatients.filter(p => {
    const patientStatus = p.status || 'Ativo';
    if (statusFilter === 'Ativos') return patientStatus === 'Ativo';
    if (statusFilter === 'Inativos') return patientStatus === 'Inativo';
    return true; // 'Todos'
  });

  const selectedPatients = patients.filter(p => selectedIds.includes(p.id));
  const selectedPatientsHaveActive = selectedPatients.some(p => (p.status || 'Ativo') === 'Ativo');
  const selectedPatientsHaveInactive = selectedPatients.some(p => p.status === 'Inativo');

  const allDisplayedSelected = displayedPatients.length > 0 && displayedPatients.every(p => selectedIds.includes(p.id));

  const handleSelectAllToggle = () => {
    if (allDisplayedSelected) {
      setSelectedIds(prev => prev.filter(id => !displayedPatients.some(p => p.id === id)));
    } else {
      const newIds = [...selectedIds];
      displayedPatients.forEach(p => {
        if (!newIds.includes(p.id)) {
          newIds.push(p.id);
        }
      });
      setSelectedIds(newIds);
    }
  };

  const handleBulkInactivate = () => {
    if (onTogglePatientsStatus) {
      onTogglePatientsStatus(selectedIds, 'Inativo');
      setSelectedIds([]);
    }
  };

  const handleBulkActivate = () => {
    if (onTogglePatientsStatus) {
      onTogglePatientsStatus(selectedIds, 'Ativo');
      setSelectedIds([]);
    }
  };

  const handleBulkDelete = () => {
    if (onDeletePatients) {
      onDeletePatients(selectedIds);
      setSelectedIds([]);
    }
  };

  const handleViewReport = (e: React.MouseEvent, patient: Patient) => {
    e.stopPropagation();
    if ((patient.cycles?.length ?? 0) > 0) {
      setSelectedReport({
        patient,
        cycle: patient.cycles[patient.cycles.length - 1]
      });
    } else {
      alert("Este paciente ainda não possui ciclos registrados.");
    }
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length === 0) return '';
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="space-y-4 w-full md:w-auto">
          <div>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">Meus Pacientes</h3>
            {filterAlertsOnly && (
              <div className="flex items-center gap-2 mt-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-4 py-2 rounded-xl text-amber-700 dark:text-amber-400 text-[10px] font-black uppercase tracking-wider w-fit animate-in fade-in duration-300">
                <span>⚠️ Filtrando por alertas ativos</span>
                <button 
                  onClick={onClearFilterAlerts}
                  className="ml-2 underline hover:text-amber-950 dark:hover:text-amber-200 transition-colors font-black cursor-pointer"
                >
                  Ver todos
                </button>
              </div>
            )}
          </div>

          {/* Status Tabs with count badges */}
          <div className="flex flex-wrap gap-2">
            {(['Ativos', 'Inativos', 'Todos'] as const).map(status => {
              const count = patients.filter(p => {
                const patientStatus = p.status || 'Ativo';
                if (status === 'Ativos') return patientStatus === 'Ativo';
                if (status === 'Inativos') return patientStatus === 'Inativo';
                return true;
              }).length;

              return (
                <button
                  key={status}
                  onClick={() => { setStatusFilter(status); setSelectedIds([]); }}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${
                    statusFilter === status 
                      ? 'bg-indigo-600 text-white shadow-md' 
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {status} <span className="opacity-60 ml-1">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {showProfessionalFilter && (
            <div className="relative w-full sm:w-64">
              <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              <select
                aria-label="Filtrar por profissional"
                value={professionalFilter}
                onChange={(e) => { setProfessionalFilter(e.target.value); setSelectedIds([]); }}
                className="w-full pl-12 pr-10 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 transition-all dark:text-slate-200 appearance-none cursor-pointer"
              >
                <option value="all">Todos os profissionais</option>
                {professionals.map((prof) => (
                  <option key={prof.id} value={prof.id}>{prof.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>
          )}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text" 
            placeholder="Buscar por nome..." 
            className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 transition-all dark:text-slate-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        </div>
      </div>

      {/* Floating/Sliding Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: -15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -15, scale: 0.98 }}
          className="bg-indigo-50 dark:bg-slate-900 border-2 border-indigo-100 dark:border-indigo-950 rounded-[2rem] p-6 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-xl shadow-indigo-600/5 animate-in fade-in duration-300"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black text-xs shadow-md">
              {selectedIds.length}
            </div>
            <div>
              <p className="text-sm font-black text-slate-800 dark:text-slate-100">Paciente(s) selecionado(s)</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Ações em lote para os pacientes selecionados</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 w-full sm:w-auto">
            {selectedPatientsHaveActive && (
              <button
                onClick={handleBulkInactivate}
                className="flex-1 sm:flex-none px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all hover:scale-105 active:scale-95 shadow-md shadow-amber-500/10 cursor-pointer flex items-center justify-center gap-2"
              >
                <UserX size={13} /> Inativar Selecionados
              </button>
            )}
            {selectedPatientsHaveInactive && (
              <button
                onClick={handleBulkActivate}
                className="flex-1 sm:flex-none px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all hover:scale-105 active:scale-95 shadow-md shadow-emerald-500/10 cursor-pointer flex items-center justify-center gap-2"
              >
                <UserCheck size={13} /> Reativar Selecionados
              </button>
            )}
            <button
              onClick={handleBulkDelete}
              className="flex-1 sm:flex-none px-5 py-3 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all hover:scale-105 active:scale-95 shadow-md shadow-red-600/10 cursor-pointer flex items-center justify-center gap-2"
            >
              <Trash2 size={13} /> Apagar Selecionados
            </button>
          </div>
        </motion.div>
      )}

      <div className="overflow-hidden bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
              <th className="px-3 md:px-6 py-5 text-center w-12" onClick={(e) => e.stopPropagation()}>
                <input 
                  type="checkbox"
                  checked={allDisplayedSelected}
                  onChange={handleSelectAllToggle}
                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                />
              </th>
              <th className="px-4 md:px-8 py-5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Paciente</th>
              <th className="hidden md:table-cell px-4 md:px-8 py-5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Abordagem</th>
              <th className="hidden lg:table-cell px-4 md:px-8 py-5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] text-center">Sessões realizadas</th>
              <th className="hidden md:table-cell px-4 md:px-8 py-5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] text-center">Ciclos</th>
              <th className="hidden sm:table-cell px-4 md:px-8 py-5 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] text-right">Ações Rápidas</th>
              <th className="px-4 py-5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {displayedPatients.map((patient) => {
              const isSelected = selectedIds.includes(patient.id);
              return (
                <tr 
                  key={patient.id} 
                  className={`hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer group transition-colors ${isSelected ? 'bg-indigo-50/20 dark:bg-indigo-900/10' : ''}`}
                  onClick={() => onSelectPatient(patient.id)}
                >
                  <td className="px-3 md:px-6 py-6 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedIds(prev => 
                          prev.includes(patient.id) ? prev.filter(id => id !== patient.id) : [...prev, patient.id]
                        );
                      }}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                    />
                  </td>
                  <td className="px-4 md:px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-slate-800 flex items-center justify-center text-indigo-700 dark:text-indigo-400 font-black text-xs uppercase shadow-sm">
                          {getInitials(patient.name)}
                        </div>
                        {/* Excluir: canto superior esquerdo, oposto ao lápis. */}
                        {onDeletePatient && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPatientToDelete(patient);
                            }}
                            className="absolute -top-1 -left-1 p-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-md hover:scale-110 active:scale-95 transition-all z-10 border border-white dark:border-slate-900 cursor-pointer"
                            title="Excluir Paciente"
                          >
                            <Trash2 size={10} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectPatient(patient.id, true);
                          }}
                          className="absolute -bottom-1 -right-1 p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-md hover:scale-110 active:scale-95 transition-all z-10 border border-white dark:border-slate-900 cursor-pointer"
                          title="Editar Paciente"
                        >
                          <Pencil size={10} />
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-black text-slate-900 dark:text-slate-100 block">{patient.name}</span>
                          {(patient.status || 'Ativo') === 'Inativo' && (
                            <span className="text-[8px] bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full font-black uppercase tracking-wider">Inativo</span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-black uppercase tracking-widest">{patient.age || '?'} ANOS</span>
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-4 md:px-8 py-6">
                    <span className="text-[10px] px-4 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800">
                      {patient.activeApproach.split('(')[0]}
                    </span>
                  </td>
                  <td className="hidden lg:table-cell px-4 md:px-8 py-6 text-center">
                    <span className="text-xs font-black text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">{patient.records?.length || 0}</span>
                  </td>
                  <td className="hidden md:table-cell px-4 md:px-8 py-6 text-center">
                    <span className="text-xs font-black text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700">{patient.cycles?.length ?? 0}</span>
                  </td>
                  <td className="hidden sm:table-cell px-4 md:px-8 py-6 text-right">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectPatient(patient.id, true);
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 rounded-xl transition-all border-2 border-indigo-100 dark:border-indigo-900/50"
                    >
                      <FileText size={14} /> Cadastro do Paciente
                    </button>
                  </td>
                  <td className="px-3 md:px-6 py-6 text-right">
                    <ChevronRight size={22} className="text-slate-300 dark:text-slate-700 group-hover:text-indigo-500 transition-colors" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedPatients.length === 0 && (
          <div className="p-10 sm:p-20 text-center">
            <p className="text-sm font-black text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em] italic">
              Nenhum paciente encontrado para os critérios selecionados.
            </p>
          </div>
        )}
      </div>

      {selectedReport && (
        <ReportModal 
          patient={selectedReport.patient}
          cycle={selectedReport.cycle}
          therapist={therapist}
          onClose={() => setSelectedReport(null)}
        />
      )}

      {/* Confirmação de exclusão individual */}
      {patientToDelete && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-4 text-rose-500">
              <div className="w-12 h-12 bg-rose-50 dark:bg-rose-950/30 rounded-2xl flex items-center justify-center shrink-0">
                <AlertTriangle size={24} />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight">Excluir paciente</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Tem certeza que deseja excluir o paciente <strong className="text-slate-900 dark:text-white">{patientToDelete.name}</strong>?
              Prontuário, ciclos, gravações e agendamentos vinculados serão removidos. Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setPatientToDelete(null)}
                disabled={isDeleting}
                className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeletePatient}
                disabled={isDeleting}
                className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-widest shadow-lg transition-all cursor-pointer disabled:opacity-60"
              >
                {isDeleting ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientList;
